(() => {
  if (customElements.get('liene-deal-rows')) return;
  customElements.define('liene-deal-rows', class extends HTMLElement {
    connectedCallback() {
      this.controller = new AbortController();
      this.rows = [...this.querySelectorAll('.ldp-popular__row')];
      this.observer = new ResizeObserver(() => this.update());
      this.rows.forEach(row => {
        const track = row.querySelector('.ldp-popular__track');
        track.addEventListener('scroll', () => this.updateRow(row), { passive: true, signal: this.controller.signal });
        this.observer.observe(track);
        [...track.children].forEach(card => this.observer.observe(card));
      });
      this.addEventListener('click', event => {
        const button = event.target.closest('[data-direction]');
        if (!button || button.disabled) return;
        const track = button.closest('.ldp-popular__row').querySelector('.ldp-popular__track');
        const direction = getComputedStyle(track).direction === 'rtl' ? -1 : 1;
        const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
        track.scrollBy({ left: Number(button.dataset.direction) * direction * (track.clientWidth + gap), behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' });
      }, { signal: this.controller.signal });
      this.addEventListener('shopify:block:select', event => {
        const card = event.target.closest('.ldp__card');
        if (!card) return;
        const track = card.closest('.ldp-popular__track');
        const rect = card.getBoundingClientRect(), bounds = track.getBoundingClientRect();
        if (rect.left < bounds.left || rect.right > bounds.right) track.scrollBy({ left: rect.left - bounds.left, behavior: 'instant' });
      }, { signal: this.controller.signal });
      this.update();
    }
    updateRow(row) {
      const track = row.querySelector('.ldp-popular__track');
      const max = track.scrollWidth - track.clientWidth;
      const position = Math.abs(track.scrollLeft);
      row.querySelector('.ldp-popular__controls').hidden = max <= 2;
      row.querySelector('[data-direction="-1"]').disabled = position <= 2;
      row.querySelector('[data-direction="1"]').disabled = position >= max - 2;
    }
    update() { this.rows.forEach(row => this.updateRow(row)); }
    disconnectedCallback() { this.controller.abort(); this.observer.disconnect(); }
  });
})();

(() => {
  if (customElements.get('liene-popular-pagination')) return;
  customElements.define('liene-popular-pagination', class extends HTMLElement {
    connectedCallback() {
      // The theme editor can connect the element before its children are parsed.
      this.initFrame = requestAnimationFrame(() => { if (this.isConnected) this.initialize(); });
    }

    initialize() {
      this.controller = new AbortController();
      this.grid = this.querySelector('.ldp-popular__grid');
      this.button = this.querySelector('[data-more-toggle]');
      if (!this.button) return;
      this.status = this.querySelector('[data-load-status]');
      this.next = this.dataset.next;
      this.extra = 0;
      this.mobile = matchMedia('(max-width: 767px)');
      this.button.setAttribute('role', 'button');
      this.button.addEventListener('click', event => { event.preventDefault(); this.toggle(); }, { signal: this.controller.signal });
      this.button.addEventListener('keydown', event => {
        if (event.key === ' ') { event.preventDefault(); this.toggle(); }
      }, { signal: this.controller.signal });
      this.mobile.addEventListener('change', () => this.render(), { signal: this.controller.signal });
      this.render();
    }

    get initial() { return Number(this.mobile.matches ? this.dataset.mobileLimit : this.dataset.desktopLimit); }
    get cards() { return [...this.grid.children]; }

    render() {
      const cards = this.cards;
      cards.forEach((card, index) => {
        card.classList.remove('ldp-popular__hide-desktop', 'ldp-popular__hide-mobile');
        card.hidden = index >= this.initial + this.extra;
      });
      this.complete = !this.next && this.initial + this.extra >= cards.length;
      this.button.hidden = !this.next && cards.length <= this.initial;
      this.button.textContent = this.complete ? this.dataset.less : this.dataset.more;
      this.button.setAttribute('aria-expanded', String(this.extra > 0));
    }

    async toggle() {
      if (this.loading) return;
      this.status.textContent = '';
      if (this.complete) {
        this.extra = 0;
        this.render();
        // Keep the collapsed section in view instead of jumping into the next section.
        const section = this.closest('liene-deal-products');
        if (section.getBoundingClientRect().top < 0) section.scrollIntoView({ block: 'start', behavior: 'instant' });
        this.button.focus({ preventScroll: true });
        return;
      }
      this.loading = true;
      this.button.setAttribute('aria-busy', 'true');
      try {
        const desired = this.initial + this.extra + 8;
        while (this.cards.length < desired && this.next) {
          const url = new URL(this.next, location.href);
          // Preserve preview, locale, and template query parameters.
          const current = new URL(location.href);
          current.searchParams.forEach((value, key) => { if (!url.searchParams.has(key)) url.searchParams.set(key, value); });
          url.searchParams.set('section_id', this.dataset.section);
          const response = await fetch(url, { signal: this.controller.signal });
          if (!response.ok) throw new Error('Unable to fetch section');
          const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
          const page = [...doc.querySelectorAll('liene-popular-pagination')].find(el => el.dataset.section === this.dataset.section);
          if (!page || page.dataset.next === this.next) throw new Error('Invalid pagination response');
          const cards = page.querySelectorAll('.ldp-popular__grid > .ldp__card');
          cards.forEach(card => { card.hidden = true; this.grid.append(card); });
          this.next = page.dataset.next;
        }
        this.extra += 8;
        this.render();
      } catch (error) {
        if (error.name !== 'AbortError') this.status.textContent = this.status.dataset.errorMessage;
      } finally {
        this.loading = false;
        this.button.removeAttribute('aria-busy');
      }
    }

    disconnectedCallback() { cancelAnimationFrame(this.initFrame); this.controller?.abort(); }
  });
})();
